import { api } from './api';
import { AudioFeatures } from '@meradio/shared-types';

export const CRATE_MAX_SLOTS = 30;

export interface CrateVideoChannel {
  id?: string;
  channelId?: string;
  name?: string;
  thumbnailUrl?: string;
}

export interface CrateVideo {
  id: string;
  videoId: string;
  title: string;
  description?: string;
  thumbnailUrl?: string;
  publishedAt: string;
  duration?: number;
  channel?: CrateVideoChannel;
  audioFeatures?: AudioFeatures | null;
}

export interface Crate {
  id: string;
  name: string;
  videoCount: number;
  videos?: CrateVideo[];
  createdAt: string;
  updatedAt: string;
}

export const getCrates = async (): Promise<Crate[]> => {
  const response = await api.get<{ crates: Crate[] }>('/crates');

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to load crates');
  }

  return response.data.crates;
};

export const getCrate = async (crateId: string): Promise<Crate> => {
  if (!crateId || crateId.trim().length === 0) {
    throw new Error('Crate ID is required');
  }

  const response = await api.get<{ crate: Crate }>(`/crates/${crateId}`);

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to load crate');
  }

  return response.data.crate;
};

export const createCrate = async (name: string): Promise<Crate> => {
  const trimmed = name.trim();
  if (trimmed.length === 0) {
    throw new Error('Crate name is required');
  }

  const response = await api.post<{ crate: Crate }>('/crates', { name: trimmed });

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to create crate');
  }

  return response.data.crate;
};

export const renameCrate = async (crateId: string, name: string): Promise<Crate> => {
  const trimmed = name.trim();
  if (!crateId || trimmed.length === 0) {
    throw new Error('Crate ID and name are required');
  }

  const response = await api.put<{ crate: Crate }>(`/crates/${crateId}`, { name: trimmed });

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to rename crate');
  }

  return response.data.crate;
};

export const deleteCrate = async (crateId: string): Promise<void> => {
  if (!crateId || crateId.trim().length === 0) {
    throw new Error('Crate ID is required');
  }

  const response = await api.delete(`/crates/${crateId}`);

  if (!response.success) {
    throw new Error(response.error || 'Failed to delete crate');
  }
};

export const addVideoToCrate = async (crateId: string, videoId: string): Promise<Crate> => {
  if (!crateId || !videoId) {
    throw new Error('Crate ID and video ID are required');
  }

  const response = await api.post<{ crate: Crate }>(`/crates/${crateId}/videos`, { videoId });

  if (!response.success || !response.data) {
    throw new Error(response.error || response.message || 'Failed to add to crate');
  }

  return response.data.crate;
};

export const removeVideoFromCrate = async (crateId: string, videoId: string): Promise<Crate> => {
  if (!crateId || !videoId) {
    throw new Error('Crate ID and video ID are required');
  }

  const response = await api.delete<{ crate: Crate }>(`/crates/${crateId}/videos/${videoId}`);

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to remove from crate');
  }

  return response.data.crate;
};
